import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import Icon from "../AppIcon";
import Button from "./Button";
import { useTheme } from "../../contexts/ThemeContext";

const ThemeToggle = () => {
  const { toggleTheme, isDark } = useTheme();

  return (
    <Button variant="ghost" size="sm" onClick={toggleTheme} className="p-2">
      <Icon name={isDark ? "Sun" : "Moon"} size={16} />
    </Button>
  );
};

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const { theme, toggleTheme, isDark } = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navigationItems = [
    { path: "/", label: "Home", icon: "Terminal" },
    { path: "/about-me", label: "About Me", icon: "User" },
    { path: "/dj-studio", label: "DJ Studio", icon: "Sliders" },
    { path: "/contact-me", label: "Contact Me", icon: "Mail" }
  ];

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  // Show remaining session time from localStorage
  const SessionTimer = () => {
    const [remaining, setRemaining] = useState(0);

    useEffect(() => {
      const updateRemaining = () => {
        const bootSession = localStorage.getItem("bootSession");
        if (bootSession) {
          const sessionTime = parseInt(bootSession, 10);
          const elapsed = Date.now() - sessionTime;
          const total = 5 * 60 * 1000;
          const left = Math.max(total - elapsed, 0);
          setRemaining(left);
        } else {
          setRemaining(0);
        }
      };
      updateRemaining();
      const interval = setInterval(updateRemaining, 1000);
      return () => clearInterval(interval);
    }, []);

    if (remaining <= 0) return <span>Session expired</span>;
    const minutes = Math.floor(remaining / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);
    return (
      <span>
        {minutes}:{seconds.toString().padStart(2, "0")} left
      </span>
    );
  };

  const Logo = () => (
    <Link to="/" className="flex items-center space-x-2 group">
      <div className="relative">
        <div className="w-8 h-8 bg-primary rounded border border-primary flex items-center justify-center terminal-glow group-hover:terminal-glow-strong transition-all duration-300">
          <span className="text-primary-foreground font-fira font-bold text-sm">
            DN
          </span>
        </div>
        <div className="absolute inset-0 bg-primary rounded opacity-20 animate-pulse"></div>
      </div>
      <div className="hidden sm:block">
        <span className="font-fira font-bold text-lg text-foreground group-hover:text-primary transition-colors duration-300">
          DeveloperNoob
        </span>
        <div className="text-xs text-muted-foreground font-source">
          <div className="flex items-center space-x-1">
            <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
            <Icon name="Wifi" size={12} />
            <span>Connected</span>
            <div className="flex items-center space-x-1">
              <Icon name="Clock" size={12} />
               <span>{new Date()?.toLocaleTimeString()}</span>
              {/* <SessionTimer /> */}
            </div>
          </div>
        </div>
      </div>
    </Link>
  );

  const NavLink = ({ item, isMobile = false }) => (
    <Link
      to={item?.path}
      className={`
        flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 theme-transition
        ${
          isActivePath(item?.path)
            ? "bg-primary/10 text-primary border border-primary/20 terminal-glow"
            : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
        }
        ${isMobile ? "w-full justify-start" : ""}
      `}
      onClick={() => isMobile && setIsMenuOpen(false)}
    >
      <Icon name={item?.icon} size={16} />
      <span className="font-inter">{item?.label}</span>
      {isActivePath(item?.path) && (
        <div className="w-1 h-1 bg-primary rounded-full animate-pulse"></div>
      )}
    </Link>
  );

  return (
    <header
      className={`
        fixed top-0 left-0 right-0 z-50 transition-all duration-300 theme-transition
        ${
          isScrolled
            ? "bg-background/95 backdrop-blur-terminal border-b border-border terminal-glow"
            : "bg-background/80 backdrop-blur-sm"
        }
      `}
    >
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Logo />

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1">
            {navigationItems?.map((item) => (
              <NavLink key={item?.path} item={item} />
            ))}
            {/* <div className="ml-4 pl-4 border-l border-border">
              <ThemeToggle />
            </div> */}
          </nav>

          {/* Mobile Menu Button and Theme Toggle */}
          <div className="md:hidden flex items-center space-x-2">
            {/* <ThemeToggle /> */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2"
            >
              <Icon name={isMenuOpen ? "X" : "Menu"} size={20} />
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-border">
            <div className="px-2 pt-2 pb-3 space-y-1 bg-background/95 backdrop-blur-terminal theme-transition">
              {navigationItems?.map((item) => (
                <NavLink key={item?.path} item={item} isMobile />
              ))}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
